/**
 * THE CALENDAR FAMILY SPEAKS CALENDAR DAYS, NOT INSTANTS.
 *
 * Every value in and out of `@shared/ui/calendar` is an `IsoDate` — a `'YYYY-MM-DD'` string.
 * A day has no time and no zone, so nothing here can shift it: the old pickers handed out
 * browser-local-midnight `Dayjs`/`Date` objects, and a caller that sent `toISOString()` of
 * one stored the PREVIOUS day for anyone east of UTC. Turning a day into the instant a
 * server wants (Zurich midnight for the audit period columns) is the caller's job, through
 * `@shared/lib/zurichDay`.
 *
 * Internally the grid engine needs `Date`s; those are built at LOCAL midnight and converted
 * straight back, and they never leave this folder.
 */

/** `'YYYY-MM-DD'`. A plain string so it survives JSON, URLs and equality checks unchanged. */
export type IsoDate = string;

/**
 * A range of days, both ends inclusive. Either end may be missing: a range being picked has
 * no end yet, and an open-ended period never gets one. Whether a missing end is acceptable
 * is the caller's rule, not the field's.
 */
export interface IsoDateRange {
  start: IsoDate | null;
  end: IsoDate | null;
}

export const EMPTY_RANGE: IsoDateRange = { start: null, end: null };

export type DatePart = 'day' | 'month' | 'year';

/**
 * HOW A DAY IS WRITTEN — the one place that says so: its parts in reading order and the
 * character between them. The display (`formatIsoDate`), the typed-date parser and the
 * typing mask (`dateMask.ts`) all follow it, so another country's `01/01/2024` is a change
 * here (and in the translated placeholder), not a new field. Swiss day-first and dotted,
 * as §5.10 writes dates; deliberately NOT read from the browser locale, which would turn
 * an English UI's dots into `en-GB` slashes.
 */
export const DATE_FORMAT = {
  order: ['day', 'month', 'year'],
  separator: '.',
} as const satisfies { order: readonly [DatePart, DatePart, DatePart]; separator: string };

/** Digits per part as the field writes them. */
export const DATE_PART_WIDTH: Record<DatePart, number> = { day: 2, month: 2, year: 4 };

/** The separators an auditor may type: the format's own, and the ones people use anyway. */
export const TYPED_SEPARATORS = `${DATE_FORMAT.separator}./-`;

const PART_TOKEN: Record<DatePart, string> = { day: 'DD', month: 'MM', year: 'YYYY' };

/** What the field shows and what an auditor types: `DD.MM.YYYY`. */
export const DISPLAY_FORMAT = DATE_FORMAT.order
  .map((part) => PART_TOKEN[part])
  .join(DATE_FORMAT.separator);

const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** A real calendar day, or `null` for 31.09 and friends. */
function buildDate(year: number, month: number, day: number): Date | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(year, month - 1, day);
  // `Date` rolls an impossible day into the next month (31.09 → 01.10); a rolled date is
  // not the day that was asked for.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** `IsoDate` → local-midnight `Date`, or `null` when the string is not a real day. */
export function isoToDate(iso: IsoDate | null | undefined): Date | null {
  if (!iso) return null;
  const match = ISO_PATTERN.exec(iso);
  if (!match) return null;
  return buildDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

/** Local `Date` → the `IsoDate` of the calendar day it falls on in this browser. */
export function dateToIso(date: Date): IsoDate {
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A usable `IsoDate` or `null` — anything malformed reads as empty rather than throwing. */
export function normalizeIso(iso: IsoDate | null | undefined): IsoDate | null {
  const date = isoToDate(iso);
  return date ? dateToIso(date) : null;
}

/** `'2026-12-31'` → `'31.12.2026'`; empty for a missing or malformed day. */
export function formatIsoDate(iso: IsoDate | null | undefined): string {
  const match = iso ? ISO_PATTERN.exec(iso) : null;
  if (!match || !isoToDate(iso)) return '';
  const parts: Record<DatePart, string> = { year: match[1], month: match[2], day: match[3] };
  return DATE_FORMAT.order.map((part) => parts[part]).join(DATE_FORMAT.separator);
}

/** Today in this browser, as the day the auditor is living in. */
export function todayIso(now: Date = new Date()): IsoDate {
  return dateToIso(now);
}

/** Lexicographic order IS chronological order for `IsoDate`. */
export function compareIso(a: IsoDate, b: IsoDate): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function isWithinBounds(
  iso: IsoDate,
  minDate?: IsoDate | null,
  maxDate?: IsoDate | null,
): boolean {
  if (minDate && compareIso(iso, minDate) < 0) return false;
  if (maxDate && compareIso(iso, maxDate) > 0) return false;
  return true;
}

export function rangesEqual(a: IsoDateRange, b: IsoDateRange): boolean {
  return a.start === b.start && a.end === b.end;
}

/** Both ends readable, swapped into order if they came in reversed. */
export function normalizeRange(range: IsoDateRange | null | undefined): IsoDateRange {
  const start = normalizeIso(range?.start);
  const end = normalizeIso(range?.end);
  if (start && end && compareIso(start, end) > 0) return { start: end, end: start };
  return { start, end };
}

export type TypedDate = { kind: 'empty' } | { kind: 'valid'; iso: IsoDate } | { kind: 'invalid' };

/** Three captured parts in the format's order, `between` each. */
const inFormatOrder = (part: Record<DatePart, string>, between: string): RegExp =>
  new RegExp(`^${DATE_FORMAT.order.map((name) => `(${part[name]})`).join(between)}$`);

const SEPARATOR_CLASS = `[${TYPED_SEPARATORS.split('')
  .map((char) => `\\${char}`)
  .join('')}]`;

const TYPED_PATTERNS: Array<{ pattern: RegExp; order: readonly DatePart[] }> = [
  // 31.12.2026 · 31/12/2026 · 31-12-2026 · 1.2.26 — the separators people actually type.
  {
    pattern: inFormatOrder(
      { day: '\\d{1,2}', month: '\\d{1,2}', year: '\\d{4}|\\d{2}' },
      SEPARATOR_CLASS,
    ),
    order: DATE_FORMAT.order,
  },
  // 31122026 — the digits alone.
  {
    pattern: inFormatOrder({ day: '\\d{2}', month: '\\d{2}', year: '\\d{4}' }, ''),
    order: DATE_FORMAT.order,
  },
  // 2026-12-31 — a pasted ISO date.
  { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: ['year', 'month', 'day'] },
];

/**
 * What an auditor typed → a day.
 *
 * IN THE FORMAT'S ORDER, ALWAYS: `01.02.2026` is the 1st of February, as Switzerland writes
 * it and as the field displays it. A two-digit year is this century. Anything that names no
 * real day — `31.09.2026`, `29.02.2027`, stray letters — is `invalid`, never a nearby day: a
 * field that quietly turned 31.09 into 01.10 would store a date nobody typed.
 */
export function parseTypedDate(text: string): TypedDate {
  const trimmed = text.trim();
  if (!trimmed) return { kind: 'empty' };
  for (const { pattern, order } of TYPED_PATTERNS) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    const read = (part: DatePart) => match[order.indexOf(part) + 1];
    const yearText = read('year');
    const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
    const date = buildDate(year, Number(read('month')), Number(read('day')));
    return date ? { kind: 'valid', iso: dateToIso(date) } : { kind: 'invalid' };
  }
  return { kind: 'invalid' };
}

/**
 * The typed day, once its year is written in full — `null` before that.
 *
 * `01.01.20` already reads as 1 January 2020, but mid-typing it is `01.01.2024` on its way:
 * whatever follows the typing (the open calendar, a paste being formatted) waits for the
 * whole year. A commit still accepts the two-digit year, through `parseTypedDate`.
 */
export function parseCompleteTypedDate(text: string): IsoDate | null {
  const typed = parseTypedDate(text);
  // No accepted shape gives a day or a month more than two digits: four in a row are the year.
  return typed.kind === 'valid' && /\d{4}/.test(text) ? typed.iso : null;
}

/** The first of the month a day falls in — what the grid navigates by. */
export function startOfMonthDate(iso: IsoDate | null | undefined, fallback: Date): Date {
  const date = isoToDate(iso) ?? fallback;
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(month: Date, count: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + count, 1);
}

export function monthIndex(month: Date): number {
  return month.getFullYear() * 12 + month.getMonth();
}
